/**
 * Sponsored product slot (sponsored-slot contract C1/C2): the pure rules both surfaces share —
 * where the sponsored line goes in the category grid, and how the typeahead's default state
 * keeps it out of "Recently reviewed". DealHunt picks the slot (B3) and stamps the search
 * document (B4); SoundGuys only places and labels it. Never re-ranks a typed query.
 */
import type { DealHuntCategoryHit, DealHuntSponsoredSlot, DealHuntSuggestHit } from './types'

/** SoundGuys' ethics statement (WP page) — the typeahead's "Why this?" link. */
export const SG_ETHICS_PATH = '/ethics/'

/** Sponsored rows the typeahead's default state shows (the query asks for 2: one spare when
 *  DealHunt has two live slots; the section stays one row). */
export const SPONSORED_TYPEAHEAD_ROWS = 1

/**
 * The category grid's hits: the sponsored line first, then every listing hit except that line.
 * The slot rides page 0's response only and `useCategoryBrowse` keeps it across "Load more",
 * so later pages are deduped against it too (the line can also land there naturally) but never
 * add a second sponsored card.
 */
export function spliceSponsoredHit(
  hits: DealHuntCategoryHit[],
  slot: DealHuntSponsoredSlot | null
): DealHuntCategoryHit[] {
  if (!slot) {
    return hits
  }
  const id = slot.hit.objectID
  return [slot.hit, ...hits.filter(h => h.objectID !== id)]
}

/**
 * The cards `category_page_list_view` names as `top_product` / `top_products`: the ORGANIC
 * listing, the sponsored card excluded, so the metric means the same with the slot on or off.
 */
export function organicCards<T extends { sponsored: unknown }>(cards: readonly T[]): T[] {
  return cards.filter(card => card.sponsored === null)
}

/** A suggest hit DealHunt marks as an active sponsored slot (B4: absent when none). */
export function isSponsoredSuggestHit(hit: DealHuntSuggestHit): boolean {
  return hit.sgSponsored?.active === true
}

/** Whether a typeahead row carries the in-row "Sponsored" label: every sponsored line, in either
 *  state, except the row rendered under the "Sponsored" header (the header already says it). So a
 *  second live slot sitting in Recently reviewed, or a typed match, is still labelled — a typed
 *  query is never re-ranked, the label is all a sponsored match gets. */
export function sponsoredRowLabel(hit: DealHuntSuggestHit, inSponsoredSection: boolean): boolean {
  return !inSponsoredSection && isSponsoredSuggestHit(hit)
}

/**
 * The typeahead's default state, split: the sponsored rows (empty when none) and the
 * "Recently reviewed" rows without them — one product never renders twice.
 */
export function splitDefaultSuggest(
  hits: DealHuntSuggestHit[],
  sponsored: DealHuntSuggestHit[] | undefined
): { sponsoredRows: DealHuntSuggestHit[]; reviewedRows: DealHuntSuggestHit[] } {
  const sponsoredRows = (sponsored ?? []).slice(0, SPONSORED_TYPEAHEAD_ROWS)
  if (sponsoredRows.length === 0) {
    return { sponsoredRows, reviewedRows: hits }
  }
  const ids = new Set(sponsoredRows.map(h => h.objectID))
  return { sponsoredRows, reviewedRows: hits.filter(h => !ids.has(h.objectID)) }
}
